import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Spin } from 'antd';
import { FolderKanban, Plus, CheckCircle2, Users } from 'lucide-react';
import { useIntl } from 'react-intl';
import {
  routes,
  STUDY_TABS,
  USER_ROLES,
  STUDY_STATUS,
  TASK_STATUS,
} from '../../utils/constants/config';
import { isEmpty } from '../../utils/helpers';
import { getProjects } from './ProjectAction';
import { hasRolePO } from '../system/systemAction';
import CreateProjectModal from './CreateProjectModal';
import './Project.scss';

/* ── Figma-extracted design tokens ──────────────────────────────── */
const C = {
  cardBg: '#2d3038',
  cardBgMid: '#25282e',
  cardBgEnd: '#1e2127',
  cardBorder: '#3a3d44',
  teal: '#2a5f5a',
  tealHover: '#245249',
  tealAlpha10: 'rgba(42,95,90,0.1)',
  tealAlpha20: 'rgba(42,95,90,0.2)',
  tealAlpha30: 'rgba(42,95,90,0.3)',
  tealAlpha50: 'rgba(42,95,90,0.5)',
  tealShadow: 'rgba(42,95,90,0.1)',
  white: '#ffffff',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  /* Figma status badge: Active = emerald-500, Completed = blue-500 */
  emerald400: '#34d399',
  emeraldBg: 'rgba(16,185,129,0.2)',
  emeraldBorder: 'rgba(16,185,129,0.3)',
  blue400: '#60a5fa',
  blueBg: 'rgba(59,130,246,0.2)',
  blueBorder: 'rgba(59,130,246,0.3)',
  /* Footer icon bg */
  blueIconBg: 'rgba(59,130,246,0.1)',
  blue400Icon: '#60a5fa',
  purpleIconBg: 'rgba(168,85,247,0.1)',
  purple400: '#c084fc',
};

/* SVG circular progress constants — Figma: w-20 h-20, r=45, strokeWidth=6 */
const RING_RADIUS = 45;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/* ── Inline styles (all from Figma) ─────────────────────────────── */
const S = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: 24,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: C.white,
    margin: 0,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    color: C.gray400,
    margin: 0,
  },
  newBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: C.teal,
    color: C.white,
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    boxShadow: `0 10px 15px -3px ${C.tealShadow}`,
  },
  /* Figma: grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 */
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: 16,
  },
  /* Figma: p-5 (20px) rounded-xl (12px) h-[260px] w fixed */
  card: {
    position: 'relative',
    padding: 20,
    borderRadius: 12,
    cursor: 'pointer',
    background: `linear-gradient(135deg, ${C.cardBg}, ${C.cardBgMid}, ${C.cardBgEnd})`,
    border: `1px solid ${C.cardBorder}`,
    transition: 'all 0.3s',
    height: 260,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  /* Status badge row — Figma: flex justify-end mb-3 */
  badgeRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: 12,
    flexShrink: 0,
  },
  /* Figma: px-2.5 py-0.5 rounded-full text-xs font-bold */
  badge: {
    padding: '2px 10px',
    borderRadius: 9999,
    fontSize: 12,
    fontWeight: 700,
  },
  activeBadge: {
    background: C.emeraldBg,
    color: C.emerald400,
    border: `1px solid ${C.emeraldBorder}`,
  },
  completedBadge: {
    background: C.blueBg,
    color: C.blue400,
    border: `1px solid ${C.blueBorder}`,
  },
  /* Figma: flex items-center justify-center mb-3 */
  ringRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    flexShrink: 0,
  },
  ringContainer: {
    position: 'relative',
    width: 80,
    height: 80,
  },
  /* Figma: text-xl font-bold text-white => 20px 700 #fff */
  ringPct: {
    fontSize: 20,
    fontWeight: 700,
    color: C.white,
    lineHeight: 1,
  },
  /* Figma: text-[10px] text-gray-500 => 10px #6b7280 */
  ringLabel: {
    fontSize: 10,
    color: C.gray500,
    lineHeight: 1,
    marginTop: 2,
  },
  ringCenter: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  /* Figma: text-center mb-4 flex-shrink-0 px-2 */
  projectInfo: {
    textAlign: 'center',
    marginBottom: 16,
    flexShrink: 0,
    padding: '0 8px',
  },
  /* Figma: flex items-center justify-center gap-2 mb-1.5 */
  iconBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 6,
  },
  iconBadge: {
    padding: 4,
    borderRadius: 6,
    background: C.tealAlpha20,
    display: 'inline-flex',
  },
  /* Figma: text-sm font-bold text-white => 14px 700 #fff, text-center */
  cardTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: C.white,
    textAlign: 'center',
    marginBottom: 4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    transition: 'color 0.3s',
  },
  /* Figma: text-xs text-gray-400 => 12px #9ca3af, line-clamp-2, min-h-[32px] */
  cardDesc: {
    fontSize: 12,
    color: C.gray400,
    minHeight: 32,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    margin: 0,
  },
  /* Figma: border-t border-[#3a3d44] pt-3 pb-3, gap-3, flex center */
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    paddingTop: 12,
    paddingBottom: 12,
    borderTop: `1px solid ${C.cardBorder}`,
    flexShrink: 0,
    marginTop: 'auto',
  },
  footerStat: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  /* Figma: p-1 rounded-md bg-blue-500/10 */
  taskIconBg: {
    padding: 4,
    borderRadius: 6,
    background: C.blueIconBg,
    display: 'inline-flex',
  },
  userIconBg: {
    padding: 4,
    borderRadius: 6,
    background: C.purpleIconBg,
    display: 'inline-flex',
  },
  /* Figma: text-xs text-gray-400, value font-semibold text-white */
  footerText: {
    fontSize: 12,
    color: C.gray400,
  },
  footerValue: {
    fontWeight: 600,
    color: C.white,
  },
  /* Figma: w-px h-3 bg-[#3a3d44] */
  footerDivider: {
    width: 1,
    height: 12,
    background: C.cardBorder,
  },
  /* ── Create New Project card ── */
  /* Figma: p-4 (16px) rounded-xl (12px) border-2 dashed border-[#3a3d44] h-[260px] */
  dashedCard: {
    position: 'relative',
    padding: 16,
    borderRadius: 12,
    cursor: 'pointer',
    background: 'transparent',
    border: `2px dashed ${C.cardBorder}`,
    transition: 'all 0.3s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: 260,
    boxSizing: 'border-box',
  },
  /* Figma: w-12 h-12 (48px) rounded-full mb-3, border-2 border-[#2a5f5a]/30 */
  dashedCircle: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    border: `2px solid ${C.tealAlpha30}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    transition: 'all 0.3s',
  },
  /* Figma: text-sm font-semibold text-gray-400 => 14px 600 #9ca3af */
  dashedLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: C.gray400,
  },
  /* Figma: text-xs text-gray-500 mt-1 => 12px #6b7280 */
  dashedSub: {
    fontSize: 12,
    color: C.gray500,
    marginTop: 4,
  },
};

let params = { _offset: 0, _limit: 25 };

const Project = (props) => {
  const { history, userInfo, viewMode } = props;
  const { formatMessage: t } = useIntl();
  const [isOpenModal, setOpenModal] = useState(false);
  const [projectData, setProjectData] = useState({});
  const [isFetchingData, setFetchingData] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  const isViewAsPO = viewMode === USER_ROLES.PROJECT_OWNER && hasRolePO(userInfo);

  useEffect(() => {
    if (!isEmpty(userInfo) && viewMode) {
      if (isViewAsPO) {
        params.role = [USER_ROLES.PROJECT_OWNER];
      } else {
        params.role = [USER_ROLES.ANNOTATOR, USER_ROLES.REVIEWER];
      }
      params._offset = 0;
      handleGetProjects(params);
    }

    return () => {
      params = { _offset: 0, _limit: 25 };
    };
    // eslint-disable-next-line
  }, [userInfo, viewMode]);

  const handleGetProjects = async (newParams = {}, isLoadmore) => {
    try {
      setFetchingData(true);
      const { data = {} } = await getProjects({
        ...newParams,
        _offset: newParams._offset * newParams._limit,
      });

      if (isLoadmore) {
        const currentData = projectData?.data || [];
        const newData = [...currentData, ...(data.data || [])];
        setHasMore(newData.length < data.count);
        setProjectData({ ...data, data: newData });
      } else {
        setHasMore((data.data || []).length < data.count);
        setProjectData({ ...data });
      }

      setFetchingData(false);
    } catch (error) {
      console.log(error);
      setFetchingData(false);
    }
  };

  const handleClickProject = (projectId) => {
    history.push(routes.STUDY_LIST + '/' + projectId);
  };

  const createProjectSuccess = (project) => {
    setOpenModal(false);
    if (project?.data?.id) {
      props.history.push(
        routes.STUDY_LIST +
          '/' +
          project?.data?.id +
          '?tab=' +
          STUDY_TABS.SETTING.toLocaleLowerCase()
      );
    }
  };

  const handleGetStats = (data = {}) => {
    let total = 0;
    let numOfCompleted = 0;

    try {
      Object.keys(data).forEach((key) => {
        total += data[key];
      });

      if (isViewAsPO) {
        numOfCompleted = data[STUDY_STATUS.COMPLETED] || 0;
      } else {
        numOfCompleted = data[TASK_STATUS.COMPLETED] || 0;
      }
    } catch (error) {}

    const percent = (total === 0 ? 0 : numOfCompleted / total) * 100;

    const content = isViewAsPO
      ? `${numOfCompleted}/${total} ${t({ id: 'IDS_STUDIES_LOWER_CASE' })}`
      : `${numOfCompleted}/${total} ${t({ id: 'IDS_TASKS' })}`;

    return { total, numOfCompleted, percent: Math.round(percent), content };
  };

  const projects = projectData?.data || [];

  /* Determine status label from stats */
  const getStatus = (stats) => {
    if (stats.total > 0 && stats.numOfCompleted >= stats.total) return 'Completed';
    return 'Active';
  };

  /* ── Render ──────────────────────────────────────────────────── */

  /* Radiologist / Annotator view — project cards */
  if (!isViewAsPO) {
    return (
      <div style={S.page}>
        <div style={S.header}>
          <div>
            <h1 style={S.title}>{t({ id: 'IDS_PROJECTS' })}</h1>
            <p style={S.subtitle}>Your assigned annotation and review projects</p>
          </div>
        </div>

        {isFetchingData && projects.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
            <Spin size="large" />
          </div>
        ) : (
          <div style={S.grid}>
            {projects.map((it) => {
              const stats = handleGetStats(it.meta) || {};
              const status = getStatus(stats);
              const isHover = hoveredCard === it.id;
              const offset =
                RING_CIRCUMFERENCE - (stats.percent / 100) * RING_CIRCUMFERENCE;

              return (
                <div
                  key={it.id}
                  style={{
                    ...S.card,
                    borderColor: isHover ? C.tealAlpha50 : C.cardBorder,
                    boxShadow: isHover
                      ? `0 20px 25px -5px ${C.tealShadow}`
                      : 'none',
                  }}
                  onClick={() => handleClickProject(it.id)}
                  onMouseEnter={() => setHoveredCard(it.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div style={S.badgeRow}>
                    <span
                      style={{
                        ...S.badge,
                        ...(status === 'Completed'
                          ? S.completedBadge
                          : S.activeBadge),
                      }}
                    >
                      {status}
                    </span>
                  </div>

                  <div style={S.ringRow}>
                    <div style={S.ringContainer}>
                      <svg
                        width={80}
                        height={80}
                        viewBox="0 0 100 100"
                        style={{ transform: 'rotate(-90deg)' }}
                      >
                        <circle
                          cx={50}
                          cy={50}
                          r={RING_RADIUS}
                          fill="none"
                          stroke={C.cardBorder}
                          strokeWidth={6}
                        />
                        <circle
                          cx={50}
                          cy={50}
                          r={RING_RADIUS}
                          fill="none"
                          stroke={C.teal}
                          strokeWidth={6}
                          strokeLinecap="round"
                          strokeDasharray={RING_CIRCUMFERENCE}
                          strokeDashoffset={offset}
                          style={{ transition: 'stroke-dashoffset 0.5s' }}
                        />
                      </svg>
                      <div style={S.ringCenter}>
                        <span style={S.ringPct}>{stats.percent}%</span>
                        <span style={S.ringLabel}>Complete</span>
                      </div>
                    </div>
                  </div>

                  <div style={S.projectInfo}>
                    <div style={S.iconBox}>
                      <span style={S.iconBadge}>
                        <FolderKanban size={12} color={C.teal} />
                      </span>
                    </div>
                    <h3
                      style={{
                        ...S.cardTitle,
                        color: isHover ? C.teal : C.white,
                      }}
                    >
                      {it.name}
                    </h3>
                    <p style={S.cardDesc}>
                      {it.description || 'No description'}
                    </p>
                  </div>

                  <div style={S.footer}>
                    <div style={S.footerStat}>
                      <span style={S.taskIconBg}>
                        <CheckCircle2 size={12} color={C.blue400Icon} />
                      </span>
                      <span style={S.footerText}>
                        <span style={S.footerValue}>{stats.numOfCompleted}</span>/{stats.total}
                      </span>
                    </div>
                    <div style={S.footerDivider} />
                    <div style={S.footerStat}>
                      <span style={S.userIconBg}>
                        <Users size={12} color={C.purple400} />
                      </span>
                      <span style={{ ...S.footerText, fontWeight: 600, color: C.white }}>{it.num_people || 0}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  /* ── Admin / PO view (existing card grid) ────────────────────── */
  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h1 style={S.title}>My Projects</h1>
          <p style={S.subtitle}>Create and manage research annotation projects</p>
        </div>
        {hasRolePO(userInfo) && isViewAsPO && (
          <button
            style={S.newBtn}
            onClick={() => setOpenModal(true)}
            onMouseEnter={(e) => (e.currentTarget.style.background = C.tealHover)}
            onMouseLeave={(e) => (e.currentTarget.style.background = C.teal)}
          >
            <Plus size={20} />
            New Project
          </button>
        )}
      </div>

      {/* Projects Grid */}
      {isFetchingData && projects.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <Spin size="large" />
        </div>
      ) : (
        <div style={S.grid}>
          {projects.map((it) => {
            const stats = handleGetStats(it.meta) || {};
            const status = getStatus(stats);
            const isHover = hoveredCard === it.id;
            const offset =
              RING_CIRCUMFERENCE - (stats.percent / 100) * RING_CIRCUMFERENCE;

            return (
              <div
                key={it.id}
                style={{
                  ...S.card,
                  borderColor: isHover ? C.tealAlpha50 : C.cardBorder,
                  boxShadow: isHover
                    ? `0 20px 25px -5px ${C.tealShadow}`
                    : 'none',
                }}
                onClick={() => handleClickProject(it.id)}
                onMouseEnter={() => setHoveredCard(it.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div style={S.badgeRow}>
                  <span
                    style={{
                      ...S.badge,
                      ...(status === 'Completed'
                        ? S.completedBadge
                        : S.activeBadge),
                    }}
                  >
                    {status}
                  </span>
                </div>

                <div style={S.ringRow}>
                  <div style={S.ringContainer}>
                    <svg
                      width={80}
                      height={80}
                      viewBox="0 0 100 100"
                      style={{ transform: 'rotate(-90deg)' }}
                    >
                      <circle
                        cx={50}
                        cy={50}
                        r={RING_RADIUS}
                        fill="none"
                        stroke={C.cardBorder}
                        strokeWidth={6}
                      />
                      <circle
                        cx={50}
                        cy={50}
                        r={RING_RADIUS}
                        fill="none"
                        stroke={C.teal}
                        strokeWidth={6}
                        strokeLinecap="round"
                        strokeDasharray={RING_CIRCUMFERENCE}
                        strokeDashoffset={offset}
                        style={{ transition: 'stroke-dashoffset 0.5s' }}
                      />
                    </svg>
                    <div style={S.ringCenter}>
                      <span style={S.ringPct}>{stats.percent}%</span>
                      <span style={S.ringLabel}>Complete</span>
                    </div>
                  </div>
                </div>

                <div style={S.projectInfo}>
                  <div style={S.iconBox}>
                    <span style={S.iconBadge}>
                      <FolderKanban size={12} color={C.teal} />
                    </span>
                  </div>
                  <h3
                    style={{
                      ...S.cardTitle,
                      color: isHover ? C.teal : C.white,
                    }}
                  >
                    {it.name}
                  </h3>
                  <p style={S.cardDesc}>
                    {it.description || 'No description'}
                  </p>
                </div>

                <div style={S.footer}>
                  <div style={S.footerStat}>
                    <span style={S.taskIconBg}>
                      <CheckCircle2 size={12} color={C.blue400Icon} />
                    </span>
                    <span style={S.footerText}>
                      <span style={S.footerValue}>{stats.numOfCompleted}</span>/{stats.total}
                    </span>
                  </div>
                  <div style={S.footerDivider} />
                  <div style={S.footerStat}>
                    <span style={S.userIconBg}>
                      <Users size={12} color={C.purple400} />
                    </span>
                    <span style={{ ...S.footerText, fontWeight: 600, color: C.white }}>{it.num_people || 0}</span>
                  </div>
                </div>
              </div>
            );
          })}

          {hasRolePO(userInfo) && isViewAsPO && (
            <div
              style={{
                ...S.dashedCard,
                borderColor: hoveredCard === '_new' ? C.tealAlpha50 : C.cardBorder,
              }}
              onClick={() => setOpenModal(true)}
              onMouseEnter={() => setHoveredCard('_new')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div
                style={{
                  ...S.dashedCircle,
                  borderColor: hoveredCard === '_new' ? C.teal : C.tealAlpha30,
                }}
              >
                <Plus size={24} color={C.teal} />
              </div>
              <span
                style={{
                  ...S.dashedLabel,
                  color: hoveredCard === '_new' ? C.teal : C.gray400,
                  transition: 'color 0.3s',
                }}
              >
                Create New Project
              </span>
              <span style={S.dashedSub}>Start a new annotation project</span>
            </div>
          )}
        </div>
      )}

      {isOpenModal && (
        <CreateProjectModal
          onCancel={() => setOpenModal(false)}
          onOk={createProjectSuccess}
        />
      )}
    </div>
  );
};

export default connect(
  (state) => ({
    userInfo: state.system.profile,
    viewMode: state.system.viewMode,
  }),
  {}
)(withRouter(Project));
